import {AsyncPipe} from '@angular/common'
import {Component, inject, OnInit} from '@angular/core'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatCard, MatCardActions, MatCardContent} from '@angular/material/card'
import {MatIcon} from '@angular/material/icon'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {BehaviorSubject} from 'rxjs'
import {filter, first} from 'rxjs/operators'
import {BuildingTypes} from '../../model/kalp-building'
import {KalpLoan} from '../../model/kalp-loan'
import {DataService, SaveDataLoan} from '../../services/data.service'
import {KalpService, ObjectTypes, Scenarios} from '../../services/kalp.service'
import {ProposalService} from '../../services/proposal.service'
import {LoanFilterPipe} from './loan-filter.pipe'
import {LoanComponent} from './loan/loan.component'

export interface Property {
  name: string
  type: BuildingTypes
  id: string
}

@Component({
  selector: 'spb-existing-loan',
  templateUrl: './existing-loan.component.html',
  styleUrls: ['./existing-loan.component.scss'],
  imports: [LoanComponent, MatButton, AsyncPipe, LoanFilterPipe, MatIcon, MatIconButton, MatCardActions, MatCard, MatCardContent]
})
export class ExistingLoanComponent implements OnInit {

  public loans$: BehaviorSubject<KalpLoan[]> = new BehaviorSubject<KalpLoan[]>([])

  public properties: Property[] = []

  public conditions = false

  /**
   * If we should show the terms select box on the loan.
   * It should be shown when scneario is "Increase" or "Omsättning".
   */
  public showTerms = false

  private scenario: Scenarios = Scenarios.Purchase

  private readonly kalpService = inject(KalpService)
  private readonly dataService = inject(DataService)
  private readonly proposalService = inject(ProposalService)

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  public ngOnInit(): void {
    this.updateBuildings()
    this.kalpService.scenarioChange$.pipe(
      this.takeUntilDestroyed(),
      filter(Boolean)
    ).subscribe(scenario => {
      this.conditions = scenario === Scenarios.Conditions

      this.showTerms =
        [Scenarios.Increase, Scenarios.Conditions, Scenarios.Transfer].includes(scenario as Scenarios)
      this.scenario = scenario as Scenarios
    })

    this.dataService.data$
      .pipe(this.takeUntilDestroyed())
      .subscribe(data => {
        if (data) {
          const loans = Object.values(data.loans)
            .map((ls: SaveDataLoan[]) =>
              ls.map((subLoan: any) => {
                const loan = new KalpLoan(0)
                loan.setFromInput(subLoan)
                return loan
              }))
          this.loans$.next(loans[1].concat(loans[0]))
        }
        this.changes({} as any) // we must emit changes
      })
  }

  /**
   * Removes a loan based on ID
   */
  public removeLoan(id: string): void {
    this.loans$.next(this.loans$.value.filter((loan: KalpLoan) => loan.id !== id))
    this.changes(undefined)
  }

  public addLoan(isBlancoLoan: boolean): void {
    const loan = new KalpLoan(0)
    if (isBlancoLoan) {
      loan.interestPercent = 0.08
      loan.isBlancoLoan = true
    }

    this.loans$.next(this.loans$.value.concat([loan]))
    this.changes(undefined)
  }


  public changes(loan: KalpLoan | undefined): void {
    this.loans$
      .pipe(first())
      .subscribe(loans => {
        loans.forEach((l: KalpLoan) => {
          if (loan && l.id === loan.id) {
            l.setFromInput(loan)
          }
        })
        this.kalpService.loanInput$.next(loans)

        // In the event of scenario Conditions we take the loan amount from here
        if (this.scenario === Scenarios.Conditions) {
          const property = this.properties.find((p: Property) => p.type === BuildingTypes.keep)

          if (property) {
            const loanAmount = loans
              .filter((l: KalpLoan) => l.property === property.id && l.solve)
              .reduce((sum: number, l: KalpLoan) => sum + l.amount, 0)
            this.kalpService.loanAmount$.next(loanAmount)
          }
        }
        //Make a new proposal if we update other loans
        this.proposalService.makeProposal()
      })
  }

  public updateBuildings(): void {
    this.kalpService.buildings$
      .pipe(this.takeUntilDestroyed())
      .subscribe(buildings => {
        const keepers = [BuildingTypes.leave, BuildingTypes.keep]
        this.properties = buildings
          .filter(b => b.type !== BuildingTypes.purchase && b.typeOfObject !== ObjectTypes.HYRESRATT)
          .map(b => {
            const name = keepers.includes(b.type) ? 'Nuvarande' : `Övrigt boende (${b.typeOfObject})`
            return {
              name,
              type: b.type,
              id: b.id
            }
          })
      })
  }
}
